import { useEffect, useState } from "react";
import {
  Bell,
  BellRing,
  Store,
  Ticket,
  DollarSign,
  AlertCircle,
  XCircle,
  GraduationCap,
  Users,
  UserPlus,
  AlertTriangle,
  Mail,
  MailX,
  MailCheck,
  Send,
  RotateCcw,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { AdminNotification } from "@/hooks/useAdminNotifications";

const iconFor = (kind: AdminNotification["kind"]) => {
  switch (kind) {
    case "vendor_new":
    case "vendor_pending":
      return <Store className="h-4 w-4 text-primary" />;
    case "vendor_paid":
    case "ticket_paid":
    case "class_paid":
      return <DollarSign className="h-4 w-4 text-green-600" />;
    case "ticket_new":
    case "ticket_unsent":
      return <Ticket className="h-4 w-4 text-primary" />;
    case "payment_failed":
    case "class_failed":
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case "vendor_canceled":
    case "class_canceled":
      return <XCircle className="h-4 w-4 text-muted-foreground" />;
    case "class_new":
    case "class_pending":
    case "class_interest":
      return <GraduationCap className="h-4 w-4 text-primary" />;
    case "class_full":
    case "class_near_full":
      return <AlertTriangle className="h-4 w-4 text-amber-600" />;
    case "class_waitlist_new":
      return <UserPlus className="h-4 w-4 text-primary" />;
    case "class_waitlist_available":
      return <Users className="h-4 w-4 text-green-600" />;
    case "class_email_failed":
    case "email_failed":
      return <MailX className="h-4 w-4 text-destructive" />;
    case "email_stuck":
      return <Mail className="h-4 w-4 text-amber-600" />;
    case "email_resent":
      return <MailCheck className="h-4 w-4 text-green-600" />;
    case "sender_domain_error":
      return <ShieldAlert className="h-4 w-4 text-destructive" />;
    case "class_refunded":
      return <RotateCcw className="h-4 w-4 text-muted-foreground" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
};

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
};

interface Props {
  notifications: AdminNotification[];
  unreadCount: number;
  onOpenItem: (n: AdminNotification) => void;
  onMarkAllRead: () => void;
  onResend?: (messageId: string) => Promise<{ success: boolean; error?: string }>;
  isResending?: (messageId: string) => boolean;
}

const NotificationCenter = ({
  notifications,
  unreadCount,
  onOpenItem,
  onMarkAllRead,
  onResend,
  isResending,
}: Props) => {
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported",
  );

  useEffect(() => {
    if (permission === "granted" || permission === "denied" || permission === "unsupported") return;
  }, [permission]);

  const requestPerm = async () => {
    if (permission === "unsupported") return;
    const p = await Notification.requestPermission();
    setPermission(p);
  };

  const recent = notifications.slice(0, 20);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          {unreadCount > 0 ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-sm">Notifications</div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onMarkAllRead}>
                Mark all read
              </Button>
            )}
          </div>
        </div>
        {permission === "default" && (
          <div className="px-4 py-2 border-b bg-muted/40 text-xs flex items-center justify-between">
            <span className="text-muted-foreground">Get browser alerts for new activity</span>
            <Button variant="outline" size="sm" className="h-6 text-xs" onClick={requestPerm}>
              Enable
            </Button>
          </div>
        )}
        <div className="max-h-[420px] overflow-y-auto">
          {recent.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            recent.map((n) => {
              const canResend = onResend && n.itemType === "email" && n.messageId &&
                (n.kind === "email_failed" || n.kind === "email_stuck" || n.kind === "sender_domain_error");
              const busy = canResend && isResending?.(n.messageId!);
              return (
                <div
                  key={n.id}
                  className={`w-full text-left px-4 py-3 border-b hover:bg-muted/50 transition-colors flex gap-3 ${
                    !n.read ? "bg-primary/5" : ""
                  }`}
                >
                  <button
                    onClick={() => onOpenItem(n)}
                    className="flex gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className="mt-0.5 shrink-0">{iconFor(n.kind)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate">{n.title}</p>
                        {!n.read && (
                          <span className="h-2 w-2 rounded-full bg-destructive shrink-0" aria-label="unread" />
                        )}
                      </div>
                      <p className="text-sm text-foreground truncate">{n.customerName}</p>
                      <p className="text-xs text-muted-foreground truncate">{n.detail}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground">{timeAgo(n.createdAt)}</span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1">
                          {n.paymentStatus}
                        </Badge>
                        {n.emailAttemptCount && n.emailAttemptCount > 1 && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1">
                            attempt {n.emailAttemptCount}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                  {canResend && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 self-start text-xs shrink-0"
                      disabled={busy}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const res = await onResend!(n.messageId!);
                        if (res.success) {
                          toast({ title: "Resend queued", description: `Email to ${n.customerName} is being resent.` });
                        } else {
                          toast({ title: "Resend failed", description: res.error, variant: "destructive" });
                        }
                      }}
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3 mr-1" />Resend</>}
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
